"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.validProxyProtocols = exports.updatePackageMobify = exports.ssrFiles = exports.reset = exports.proxyConfigs = exports.getSSRParameters = exports.getPackageMobify = exports.MAX_PROXY_CONFIGS = void 0;
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/**
 * @module progressive-web-sdk/utils/ssr-shared
 * @private
 */
/*
 The ssr-shared-utils module is used in the PWA and in the Express app. It
 should contain ONLY the code that is required in both, to avoid adding
 to the size of vendor.js
 */

/**
 * Set of valid HTTP protocols for proxying
 * @private
 * @type {string[]}
 */
const validProxyProtocols = exports.validProxyProtocols = ['http', 'https'];

// This value is the 'mobify' object from package.json. It should be accessed
// via getPackageMobify() so that test code can update the value to test
// specific functions.
let _packageMobify;
const getPackageMobify = () => _packageMobify || {};
exports.getPackageMobify = getPackageMobify;
const getSSRParameters = () => _packageMobify && _packageMobify.ssrParameters || {};

/**
 * Maximum number of proxy configs
 * @private
 * @type {number}
 */
exports.getSSRParameters = getSSRParameters;
const MAX_PROXY_CONFIGS = exports.MAX_PROXY_CONFIGS = 8;

/**
 * An Array of objects with protocol, host, path for each configured proxy.
 * This is *only* usable server-side, where it's set when the SSRServer
 * calls updatePackageMobify.
 *
 * The objects in this array also have the following additional properties,
 * added by configureProxyConfigs in utils/ssr-server:
 * proxyPath is the full /mobify/proxy... path
 * proxy is an http-proxy-middleware function that can be used to proxy a
 * request to a target (it's an ExpressJS function taking req, res and next)
 *
 * @private
 * @type {Array}
 */
let proxyConfigs = exports.proxyConfigs = [];

/**
 * An Array with all the glob patterns from ssrOnly and ssrShared.
 * The names are relative to the 'build' directory.
 * @private
 */
let ssrFiles = exports.ssrFiles = [];

/**
 * RegExp that matches a proxy override string
 * match[1] is the protocol
 * match[3] is the host
 * match[5] (optional) is the path
 * match[6] (optional) is '/caching' for a caching proxy, undefined for a standard proxy
 * @private
 * @type {RegExp}
 */
const proxyOverrideRE = /^(http(s)?):\/\/([^/]+)(\/)?([^/]+)?(\/caching)?/;

/**
 * Updates the value of _packageMobify and dependent values.
 *
 * @private
 * @param newValue {Object} the new value of the Mobify object
 */
const updatePackageMobify = newValue => {
  _packageMobify = newValue || _packageMobify || {};

  // Clear and update the proxyConfigs array
  exports.proxyConfigs = proxyConfigs = [];
  const ssrParameters = getSSRParameters();
  const newFormatConfigs = ssrParameters.proxyConfigs || [];
  if (newFormatConfigs.length && (ssrParameters.proxyHost1 || ssrParameters.proxyHost2)) {
    throw new Error('Cannot use both proxyConfigs and old proxy declarations in ssrParameters');
  }
  if (newFormatConfigs.length > MAX_PROXY_CONFIGS) {
    throw new Error(`Cannot define more than ${MAX_PROXY_CONFIGS} proxy configurations`);
  }

  // Make proxyConfigs an Array containing all the valid proxy configs
  for (let index0 = 0; index0 < MAX_PROXY_CONFIGS; index0++) {
    const index1 = index0 + 1;
    let config;
    const defaultPath = index0 ? `base${index1}` : 'base';

    // Support the OLD format of proxy configs.
    const oldHost = ssrParameters[`proxyHost${index1}`];
    if (oldHost) {
      config = {
        /* istanbul ignore else */
        protocol: ssrParameters[`proxyProtocol${index1}`],
        host: oldHost,
        /* istanbul ignore else */
        path: ssrParameters[`proxyPath${index1}`]
      };
    } else {
      // New format of proxy configs
      config = newFormatConfigs[index0]; // may be undefined
    }

    // Allow override via environment variable
    const overrideKey = `SSR_PROXY${index1}`;
    const override = process.env[overrideKey];
    let overridden = '';
    if (override) {
      const match = proxyOverrideRE.exec(override);
      /* istanbul ignore else */
      if (match) {
        config = {
          protocol: match[1],
          host: match[3],
          path: match[5] || config && config.path
        };
        overridden = ` (overridden by ${overrideKey}="${override}")`;
      }
    }

    // If there's no config for this index, try the next one.
    if (!config) {
      continue;
    }

    // Apply defaults
    if (!config.protocol) {
      config.protocol = 'https';
    }
    if (!config.path) {
      config.path = defaultPath;
    }

    // Validate
    const msg = `Proxy config ${index1}${overridden}`;
    if (!config.host) {
      throw new Error(`${msg} has missing 'host' property`);
    }
    if (validProxyProtocols.indexOf(config.protocol) < 0) {
      throw new Error(`${msg} has invalid protocol '${config.protocol}'`);
    }
    const existing = proxyConfigs.findIndex(conf => conf.path === config.path);
    if (existing >= 0) {
      throw new Error(`${msg} ` + `has path "${config.path}" ` + `but that has already been used in config ${existing}`);
    }

    // Generate paths
    config.proxyPath = `/mobify/proxy/${config.path}`;
    config.cachingPath = `/mobify/caching/${config.path}`;
    proxyConfigs.push(config);
  }

  // Clear and reset the ssrFiles array
  exports.ssrFiles = ssrFiles = (_packageMobify.ssrOnly || []).concat(_packageMobify.ssrShared || []);
};

// For testing
exports.updatePackageMobify = updatePackageMobify;
const reset = () => {
  _packageMobify = undefined;
  exports.proxyConfigs = proxyConfigs = [];
  exports.ssrFiles = ssrFiles = [];
};
exports.reset = reset;